import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Grid, TextField, MenuItem, Button,
  Typography, IconButton, Table, TableHead, TableRow, TableCell, TableBody, Alert,
} from '@mui/material';
import { Add, Delete, Save } from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import PageHeader from '../common/PageHeader';

const EMPTY_ITEM = { description: '', sku: '', hsn_code: '', quantity: '', unit_price: '', amount: '' };
// Forwarder dropdown — FedEx India account option auto-sets fedex_account.
const FORWARDERS  = [
  { v: 'fedex_india',  label: 'FedEx India A/C — 651730414', account: '651730414' },
  { v: 'fedex_china',  label: 'FedEx China' },
  { v: 'yitong_jimmy', label: 'Yitong / Jimmy' },
  { v: 'self',         label: 'Self' },
  { v: 'other',        label: 'Other' },
];
const FREIGHT_TERMS = ['FOB', 'CIF', 'EXW', 'DDP', 'CPT', 'DAP'];
const CURRENCIES = ['USD', 'EUR', 'GBP', 'CNY', 'HKD'];
const STATUSES   = ['draft', 'ready', 'shipped', 'received', 'cancelled'];

// Increment a numeric suffix on an invoice number, or append "-1"
// "202605290001"           → "202605290001-1"
// "202605290001-1"         → "202605290001-2"
// "ABC-9"                  → "ABC-10"
function nextInvoiceNumber(s) {
  if (!s) return '';
  const m = String(s).match(/^(.*?)(-?\d+)$/);
  if (m) {
    const prefix = m[1];
    const num    = m[2];
    const isNeg  = num.startsWith('-');
    const n = parseInt(num, 10) + 1;
    if (isNeg && (prefix === '' || prefix.endsWith('-'))) {
      // Suffix like "-1" attached after a dash, e.g. "ABC-1"
      return prefix + n;
    }
    return prefix + (isNeg ? '' : '') + n;
  }
  return s + '-1';
}

export default function CreateShippingInvoice() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const cloneFromId = searchParams.get('clone');
  const navigate = useNavigate();
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [proformas, setProformas]         = useState([]);
  const [error, setError]                 = useState('');
  const [loading, setLoading]             = useState(false);

  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    invoice_number: '', po_number: '', ship_date: today, proforma_invoice_id: '',
    beneficiary_id: '',
    sender_contact_name: '', sender_phone: '', sender_address: '',
    sender_city: '', sender_country: 'China', sender_zip: '',
    receiver_contact_name: 'Adhish Parasa', receiver_phone: '+91-7827944632',
    forwarder: 'fedex_india', forwarder_notes: '',
    fedex_awb: '', fedex_account: '651730414',
    freight_terms: 'FOB', currency: 'USD', total_value: '', freight_value: '',
    status: 'draft', notes: '',
  });
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);

  useEffect(() => {
    api.get('/beneficiaries').then(r => setBeneficiaries(r.data.data || []));
    api.get('/invoices').then(r => setProformas(r.data.data || []));

    // Auto-populate next PO number in the IDTS/EX series for any NEW invoice
    // (fresh or cloned — each shipment gets its own PO number).
    if (!id) {
      api.get('/shipping-invoices/next-po-number')
        .then(r => setForm(f => ({ ...f, po_number: r.data.data.po_number })))
        .catch(() => {});
    }

    // Clone mode — load source invoice and pre-fill the form (no `id` so it'll create new)
    if (!id && cloneFromId) {
      api.get(`/shipping-invoices/${cloneFromId}`).then(r => {
        const d = r.data.data || {};
        const clean = (v) => v == null ? '' : String(v);
        setForm(f => ({
          ...f, // keeps the next-po-number fetched above (or below if it resolves later)
          invoice_number:        nextInvoiceNumber(d.invoice_number || ''),
          ship_date:             today,  // fresh date
          proforma_invoice_id:   d.proforma_invoice_id || '',
          beneficiary_id:        d.beneficiary_id || '',
          sender_contact_name:   clean(d.sender_contact_name),
          sender_phone:          clean(d.sender_phone),
          sender_address:        clean(d.sender_address),
          sender_city:           clean(d.sender_city),
          sender_country:        clean(d.sender_country) || 'China',
          sender_zip:            clean(d.sender_zip),
          receiver_contact_name: clean(d.receiver_contact_name) || 'Adhish Parasa',
          receiver_phone:        clean(d.receiver_phone) || '+91-7827944632',
          forwarder:             d.forwarder || 'fedex_india',
          forwarder_notes:       clean(d.forwarder_notes),
          fedex_awb:             '',  // fresh AWB
          fedex_account:         clean(d.fedex_account) || ((d.forwarder || 'fedex_india') === 'fedex_india' ? '651730414' : ''),
          freight_terms:         d.freight_terms || 'FOB',
          currency:              d.currency || 'USD',
          total_value:           clean(d.total_value),
          freight_value:         clean(d.freight_value),
          status:                'draft',  // always start as draft
          notes:                 clean(d.notes),
        }));
        if (d.items?.length) {
          setItems(d.items.map(i => ({
            description: i.description, sku: i.sku || '', hsn_code: i.hsn_code || '',
            quantity: i.quantity, unit_price: i.unit_price, amount: i.amount,
          })));
        }
      });
    }

    if (id) {
      api.get(`/shipping-invoices/${id}`).then(r => {
        const d = r.data.data;
        const clean = (v) => v == null ? '' : String(v);
        setForm({
          invoice_number: d.invoice_number,
          po_number: clean(d.po_number),
          ship_date: d.ship_date ? d.ship_date.substring(0, 10) : today,
          proforma_invoice_id: d.proforma_invoice_id || '',
          beneficiary_id: d.beneficiary_id || '',
          sender_contact_name: clean(d.sender_contact_name),
          sender_phone: clean(d.sender_phone),
          sender_address: clean(d.sender_address),
          sender_city: clean(d.sender_city),
          sender_country: clean(d.sender_country) || 'China',
          sender_zip: clean(d.sender_zip),
          receiver_contact_name: clean(d.receiver_contact_name) || 'Adhish Parasa',
          receiver_phone: clean(d.receiver_phone) || '+91-7827944632',
          forwarder: d.forwarder || 'fedex_india',
          forwarder_notes: clean(d.forwarder_notes),
          fedex_awb: clean(d.fedex_awb),
          fedex_account: clean(d.fedex_account) || ((d.forwarder || 'fedex_india') === 'fedex_india' ? '651730414' : ''),
          freight_terms: d.freight_terms || 'FOB',
          currency: d.currency || 'USD',
          total_value: clean(d.total_value),
          freight_value: clean(d.freight_value),
          status: d.status || 'draft',
          notes: clean(d.notes),
        });
        if (d.items?.length) {
          setItems(d.items.map(i => ({
            description: i.description, sku: i.sku || '', hsn_code: i.hsn_code || '',
            quantity: i.quantity, unit_price: i.unit_price, amount: i.amount,
          })));
        }
      });
    }
    // eslint-disable-next-line
  }, [id]);

  // When supplier changes (and not editing) — pre-fill sender fields
  useEffect(() => {
    if (id || !form.beneficiary_id) return;
    const b = beneficiaries.find(x => x.id === parseInt(form.beneficiary_id));
    if (!b) return;
    setForm(f => ({
      ...f,
      sender_contact_name: f.sender_contact_name || b.contact_person || '',
      sender_phone:        f.sender_phone        || b.phone          || '',
      sender_address:      f.sender_address      || b.address        || '',
      sender_city:         f.sender_city         || b.city           || '',
      sender_country:      f.sender_country      || b.country        || 'China',
    }));
    // eslint-disable-next-line
  }, [form.beneficiary_id, beneficiaries]);

  // Explicit proforma selection — copies header fields AND auto-populates the
  // line items + amounts from the PI. Handler (not effect) so it never clobbers
  // items loaded by edit/clone modes.
  const handleProformaChange = async (pid) => {
    setForm(f => ({ ...f, proforma_invoice_id: pid }));
    if (!pid) return;
    const p = proformas.find(x => x.id === parseInt(pid));
    if (p) {
      setForm(f => ({
        ...f,
        proforma_invoice_id: pid,
        invoice_number: p.invoice_number || f.invoice_number || '',
        currency:       p.currency || f.currency || 'USD',
        beneficiary_id: f.beneficiary_id || p.beneficiary_id || '',
      }));
    }
    try {
      const r = await api.get(`/invoices/${pid}`);
      const d = r.data.data || {};
      if (d.items?.length) {
        setItems(d.items.map(i => ({
          description: i.description || '',
          sku:         i.sku || '',
          hsn_code:    i.hsn_code || '',
          quantity:    i.quantity   != null ? String(parseFloat(i.quantity))   : '',
          unit_price:  i.unit_price != null ? String(parseFloat(i.unit_price)) : '',
          amount:      i.amount     != null ? parseFloat(i.amount).toFixed(2)  : '',
        })));
      }
    } catch { /* keep existing items if PI fetch fails */ }
  };

  const updateItem = (idx, field, val) => {
    const next = [...items];
    next[idx] = { ...next[idx], [field]: val };
    if (field === 'quantity' || field === 'unit_price') {
      const q = parseFloat(field === 'quantity' ? val : next[idx].quantity) || 0;
      const p = parseFloat(field === 'unit_price' ? val : next[idx].unit_price) || 0;
      next[idx].amount = (q * p).toFixed(2);
    }
    setItems(next);
  };
  const addItem    = () => setItems([...items, { ...EMPTY_ITEM }]);
  const removeItem = (i) => setItems(items.filter((_, j) => j !== i));

  const itemsTotal = items.reduce((s, i) => s + parseFloat(i.amount || 0), 0);
  const grandTotal = itemsTotal + parseFloat(form.freight_value || 0);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.beneficiary_id) { setError('Please select a supplier'); return; }
    if (!items.length || items.every(i => !i.description)) { setError('Add at least one item'); return; }
    setLoading(true);
    try {
      const payload = {
        ...form,
        proforma_invoice_id: form.proforma_invoice_id || null,
        total_value: grandTotal,
        freight_value: form.freight_value === '' ? null : parseFloat(form.freight_value),
        items: items.filter(i => i.description).map(i => ({
          ...i,
          quantity:   parseFloat(i.quantity || 0),
          unit_price: parseFloat(i.unit_price || 0),
          amount:     parseFloat(i.amount || 0),
        })),
      };
      if (id) await api.put(`/shipping-invoices/${id}`, payload);
      else    await api.post('/shipping-invoices', payload);
      navigate('/shipping-invoices');
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving');
    } finally { setLoading(false); }
  };

  const f = (field) => ({ value: form[field] ?? '', onChange: (e) => setForm({ ...form, [field]: e.target.value }) });

  return (
    <Box component="form" onSubmit={handleSave}>
      <PageHeader
        title={id ? 'Edit Shipping Invoice' : (cloneFromId ? 'New Shipping Invoice (cloned)' : 'New Shipping Invoice')}
        subtitle={cloneFromId
          ? 'Pre-filled from existing invoice — review and edit before saving'
          : 'Commercial invoice for a FedEx shipment from supplier'}
        breadcrumbs={[{ label: 'Shipping Invoices', path: '/shipping-invoices' }, { label: id ? 'Edit' : 'New' }]}
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => navigate('/shipping-invoices')}>Cancel</Button>
            <Button type="submit" variant="contained" startIcon={<Save />} disabled={loading}>
              {loading ? 'Saving…' : (id ? 'Update' : 'Create')}
            </Button>
          </Box>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={2}>
        {/* Header / meta */}
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 2 }}><CardContent>
            <Typography variant="subtitle1" gutterBottom>Shipment Details</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} md={4}>
                <TextField label="Invoice No. *" fullWidth {...f('invoice_number')}
                  helperText="Defaults to linked PI's number — editable" />
              </Grid>
              <Grid item xs={6} md={4}>
                <TextField label="PO No." fullWidth {...f('po_number')}
                  placeholder="e.g. IDTS/EX/26-27/005"
                  helperText="Shown next to Invoice No. on the PDF" />
              </Grid>
              <Grid item xs={6} md={4}>
                <TextField label="Ship Date *" type="date" fullWidth InputLabelProps={{ shrink: true }} {...f('ship_date')} />
              </Grid>
              <Grid item xs={6} md={4}>
                <TextField label="Status" select fullWidth {...f('status')}>
                  {STATUSES.map(s => <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField label="Supplier *" select fullWidth value={form.beneficiary_id}
                  onChange={e => setForm({ ...form, beneficiary_id: e.target.value })}>
                  {beneficiaries.map(b => <MenuItem key={b.id} value={b.id}>{b.name} — {b.country}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField label="Link to Proforma Invoice" select fullWidth value={form.proforma_invoice_id}
                  helperText="One proforma can be linked to multiple shipping invoices (for batched shipments)"
                  onChange={e => handleProformaChange(e.target.value)}>
                  <MenuItem value=""><em>— Not linked —</em></MenuItem>
                  {proformas
                    .filter(p => !form.beneficiary_id || p.beneficiary_id === parseInt(form.beneficiary_id))
                    .map(p => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.invoice_number} · {p.invoice_date} · {p.currency} {parseFloat(p.total_amount).toFixed(2)} · {p.beneficiary_name}
                      </MenuItem>
                    ))}
                </TextField>
              </Grid>
            </Grid>
          </CardContent></Card>

          {/* Sender details */}
          <Card sx={{ mb: 2 }}><CardContent>
            <Typography variant="subtitle1" gutterBottom>FROM (Sender — Supplier)</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}><TextField label="Contact Name" fullWidth {...f('sender_contact_name')} /></Grid>
              <Grid item xs={12} md={6}><TextField label="Tel" fullWidth {...f('sender_phone')} /></Grid>
              <Grid item xs={12}><TextField label="Address" fullWidth multiline rows={2} {...f('sender_address')} /></Grid>
              <Grid item xs={6} md={4}><TextField label="City" fullWidth {...f('sender_city')} /></Grid>
              <Grid item xs={6} md={4}><TextField label="Country" fullWidth {...f('sender_country')} /></Grid>
              <Grid item xs={12} md={4}><TextField label="Zip" fullWidth {...f('sender_zip')} /></Grid>
            </Grid>
          </CardContent></Card>

          {/* Forwarder */}
          <Card sx={{ mb: 2 }}><CardContent>
            <Typography variant="subtitle1" gutterBottom>Forwarder & Freight</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField label="Forwarder" select fullWidth value={form.forwarder}
                  onChange={e => {
                    const opt = FORWARDERS.find(o => o.v === e.target.value);
                    // Selecting the FedEx India option auto-sets the account number;
                    // any other forwarder clears it.
                    setForm({ ...form, forwarder: e.target.value, fedex_account: opt?.account || '' });
                  }}>
                  {FORWARDERS.map(o => <MenuItem key={o.v} value={o.v}>{o.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField label="Forwarder Notes" fullWidth {...f('forwarder_notes')}
                  placeholder="e.g. Pickup contact, instructions" />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField label="FedEx AWB" fullWidth {...f('fedex_awb')} />
              </Grid>
              <Grid item xs={6} md={4}>
                <TextField label="Freight Terms" select fullWidth {...f('freight_terms')}>
                  {FREIGHT_TERMS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={6} md={4}>
                <TextField label="Freight Value (optional)" type="number" fullWidth {...f('freight_value')}
                  helperText="Leave blank to show only the term (e.g. FOB)" />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Notes" fullWidth multiline rows={2} {...f('notes')} />
              </Grid>
            </Grid>
          </CardContent></Card>

          {/* Items */}
          <Card><CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="subtitle1">Line Items</Typography>
              <Button size="small" variant="outlined" startIcon={<Add />} onClick={addItem}>Add Item</Button>
            </Box>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 240 }}>Description</TableCell>
                    <TableCell sx={{ minWidth: 130 }}>SKU<Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 10 }}>(internal)</Typography></TableCell>
                    <TableCell sx={{ minWidth: 120 }}>HSN</TableCell>
                    <TableCell sx={{ minWidth: 100 }}>Qty</TableCell>
                    <TableCell sx={{ minWidth: 130 }}>Unit Price ({form.currency})</TableCell>
                    <TableCell sx={{ minWidth: 130 }}>Amount</TableCell>
                    <TableCell sx={{ width: 48 }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((it, idx) => (
                    <TableRow key={idx}>
                      <TableCell><TextField fullWidth value={it.description} onChange={e => updateItem(idx, 'description', e.target.value)} placeholder="e.g. Proximity Tags" /></TableCell>
                      <TableCell><TextField fullWidth value={it.sku} onChange={e => updateItem(idx, 'sku', e.target.value)} placeholder="PT-1" /></TableCell>
                      <TableCell><TextField fullWidth value={it.hsn_code} onChange={e => updateItem(idx, 'hsn_code', e.target.value)} placeholder="85235910" /></TableCell>
                      <TableCell><TextField type="number" fullWidth value={it.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)} /></TableCell>
                      <TableCell><TextField type="number" fullWidth value={it.unit_price} onChange={e => updateItem(idx, 'unit_price', e.target.value)} inputProps={{ step: '0.0001' }} /></TableCell>
                      <TableCell><TextField type="number" fullWidth value={it.amount} onChange={e => updateItem(idx, 'amount', e.target.value)} /></TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => removeItem(idx)} disabled={items.length === 1}>
                          <Delete fontSize="small" sx={{ color: items.length > 1 ? 'error.main' : 'text.disabled' }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </CardContent></Card>
        </Grid>

        {/* Summary */}
        <Grid item xs={12} md={4}>
          <Card sx={{ position: 'sticky', top: 80 }}><CardContent>
            <Typography variant="subtitle1" gutterBottom>Summary</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Items Subtotal</Typography>
              <Typography variant="body2">{form.currency} {itemsTotal.toFixed(2)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Freight</Typography>
              <Typography variant="body2">{form.freight_value ? `${form.currency} ${parseFloat(form.freight_value).toFixed(2)}` : form.freight_terms}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, pt: 1.5, borderTop: '2px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2">TOTAL</Typography>
              <Typography variant="subtitle1" fontWeight={700}>{form.currency} {grandTotal.toFixed(2)}</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              Total is calculated from the line items{form.freight_value ? ' plus freight' : ''}.
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              Receiver (us): <strong>Identium Tech Solutions Pvt Ltd</strong> · GSTIN 07AAGCI6275E1ZH · IEC AAGCI6275E
            </Typography>
          </CardContent></Card>
        </Grid>
      </Grid>
    </Box>
  );
}
